import {
  useQuery
} from "@tanstack/react-query";
import {
  ArrowRight,
  LoaderCircle
} from "lucide-react";
import {
  Link
} from "react-router";

import {
  ProductCard
} from "../product/ProductCard";
import type {
  Product
} from "../../types/catalog";

interface DynamicProductSectionProps {
  queryKey: string;
  queryFn: () => Promise<Product[]>;
  eyebrow: string;
  title: string;
  description: string;
  emptyMessage: string;
  background?: string;
}

export function DynamicProductSection({
  queryKey,
  queryFn,
  eyebrow,
  title,
  description,
  emptyMessage,
  background = "bg-[#fffdf9]"
}: DynamicProductSectionProps) {
  const productsQuery =
    useQuery({
      queryKey: [
        "homepage",
        queryKey
      ],

      queryFn
    });

  return (
    <section
      className={`${background} py-20`}
    >
      <div className="rini-container">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#70845b]">
              {eyebrow}
            </p>

            <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
              {title}
            </h2>

            <p className="mt-4 max-w-2xl text-base leading-7 text-[#746d65]">
              {description}
            </p>
          </div>

          <Link
            to="/shop"
            className="inline-flex items-center gap-2 font-semibold text-[#5e3b2c] transition hover:gap-3"
          >
            Shop all

            <ArrowRight
              size={18}
            />
          </Link>
        </div>

        {productsQuery.isLoading && (
          <div className="flex min-h-72 items-center justify-center">
            <LoaderCircle
              size={34}
              className="animate-spin text-[#70845b]"
            />
          </div>
        )}

        {productsQuery.isError && (
          <div className="mt-10 rounded-3xl bg-[#f7e6e2] p-8 text-center text-[#713f36]">
            Products could not be loaded.
          </div>
        )}

        {productsQuery.data &&
          productsQuery.data.length >
            0 && (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {productsQuery.data.map(
                (product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                  />
                )
              )}
            </div>
          )}

        {productsQuery.data?.length ===
          0 && (
          <div className="mt-10 rounded-3xl border border-dashed border-[#5e3b2c]/15 px-6 py-14 text-center">
            <p className="font-serif text-2xl text-[#38251e]">
              {emptyMessage}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
